"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  MdBusiness,
  MdPersonAdd,
  MdClose,
  MdCheckCircle,
  MdSearch,
} from "react-icons/md";
import Image from "next/image";
import styles from "./agencySection.module.css";
import { apiFetch } from "@/lib/apiFetch";
import { AgentInfo } from "../types";
import { useListingStore } from "../useListingStore";
import { useListingContext } from "../listingContex";
import { useScrollLock } from "@/hooks/use-scroll-lock";
import { useDialogRouting } from "@/hooks/use-dialog-routing";

export default function AgencySection() {
  const { data, updateField, updateMultipleFields } = useListingContext();
  const { isUserAgencyOwner } = useListingStore();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  useScrollLock(isDialogOpen);

  // If data is null, or user is NOT an agency owner, hide the section entirely
  if (!data || !isUserAgencyOwner) return null;

  const isAgencyListing = data.is_agency_listing || false;
  const selectedAgents = data.agents_info || [];

  const handleRemoveAgent = (id: string) => {
    const newInfos = selectedAgents.filter((a) => a.id !== id);
    const newIds = newInfos.map((a) => a.id);
    updateMultipleFields({
      agents_info: newInfos,
      agents_ids: newIds,
    });
  };

  return (
    <section className={styles.transparentContainer} id="agency-publishing">
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Agency Publishing</h2>
        <p className={styles.sectionSubtitle}>
          Publish this listing under your agency profile and assign specific
          agents to handle inquiries.
        </p>
      </div>

      <div
        className={`${styles.toggleCard} ${isAgencyListing ? styles.toggleCardActive : ""}`}
      >
        <div className={styles.toggleLeft}>
          <div className={styles.iconWrapper}>
            <MdBusiness size={24} />
          </div>
          <div>
            <h4 className={styles.toggleTitle}>Post as Agency Listing</h4>
            <p className={styles.toggleText}>
              Listing will show your agency branding instead of your private
              profile.
            </p>
          </div>
        </div>
        <div className={styles.toggleRight}>
          <button
            type="button"
            className={`${styles.toggleSwitch} ${isAgencyListing ? styles.toggleActive : ""}`}
            onClick={(e) => {
              e.preventDefault();
              updateField("is_agency_listing", !isAgencyListing);
            }}
          >
            <div className={styles.toggleThumb}></div>
          </button>
        </div>
      </div>

      {isAgencyListing && (
        <div className={styles.agentsWrapper}>
          <div className={styles.agentsHeader}>
            <h3 className={styles.subTitle}>
              Assigned Agents ({selectedAgents.length}/15)
            </h3>
            {selectedAgents.length < 15 && (
              <button
                type="button"
                className={styles.addBtn}
                onClick={() => setIsDialogOpen(true)}
              >
                <MdPersonAdd size={18} />
                <span>Add Agent</span>
              </button>
            )}
          </div>

          {selectedAgents.length > 0 ? (
            <div className={styles.agentsGrid}>
              {selectedAgents.map((agent) => (
                <div key={agent.id} className={styles.agentCard}>
                  <div className={styles.agentAvatar}>
                    {agent.photo_url ? (
                      <Image
                        src={agent.photo_url}
                        alt={agent.name}
                        fill
                        className={styles.avatarImg}
                      />
                    ) : (
                      <span>{agent.name.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <span className={styles.agentName}>{agent.name}</span>
                  <button
                    type="button"
                    className={styles.removeAgentBtn}
                    onClick={() => handleRemoveAgent(agent.id)}
                  >
                    <MdClose size={16} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.emptyAgents}>
              <p>
                No agents assigned yet. If none are selected, all inquiries will
                go to the default agency contact.
              </p>
            </div>
          )}
        </div>
      )}

      {isDialogOpen && (
        <AgentSelectionDialog
          onClose={() => setIsDialogOpen(false)}
          selectedInfos={selectedAgents}
          onSave={(newInfos: AgentInfo[]) => {
            updateMultipleFields({
              agents_info: newInfos,
              agents_ids: newInfos.map((a) => a.id),
            });
            setIsDialogOpen(false);
          }}
        />
      )}
    </section>
  );
}

// -------------------------------------------------------------
// DIALOG SUB-COMPONENT
// -------------------------------------------------------------
function AgentSelectionDialog({
  onClose,
  selectedInfos,
  onSave,
}: {
  onClose: () => void;
  selectedInfos: AgentInfo[];
  onSave: (infos: AgentInfo[]) => void;
}) {
  const [search, setSearch] = useState("");
  const [agents, setAgents] = useState<AgentInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [localSelected, setLocalSelected] =
    useState<AgentInfo[]>(selectedInfos);

  useDialogRouting("AgentSelectionDialog", true, onClose);

  // Search & Fetch Mock (Replace URL with your real backend route later)
  useEffect(() => {
    let active = true;
    const fetchAgents = async () => {
      setLoading(true);
      try {
        // MOCK: Replace this block with your actual API fetch!
        const res = await apiFetch(`/api/listings/getAgents?search=${search}`);
        const data = await res.json();

        if (active) {
          setAgents(data);
        }
      } catch (err) {
        console.error("Failed to fetch agents", err);
      } finally {
        if (active) setLoading(false);
      }
    };

    const timer = setTimeout(fetchAgents, 300); // Debounce
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [search]);

  const toggleAgent = (agent: AgentInfo) => {
    const isSelected = localSelected.some((a) => a.id === agent.id);
    if (isSelected) {
      setLocalSelected((prev) => prev.filter((a) => a.id !== agent.id));
    } else {
      if (localSelected.length >= 15) return; // Hard limit
      setLocalSelected((prev) => [...prev, agent]);
    }
  };

  return (
    <div className={styles.dialogOverlay} onClick={onClose}>
      <div
        className={styles.dialogContent}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <div className={styles.dialogHeader}>
          <h3 className={styles.dialogTitle}>Select Agents</h3>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            className={styles.closeBtn}
          >
            <MdClose size={24} />
          </button>
        </div>

        <div className={styles.searchWrapper}>
          <MdSearch className={styles.searchIcon} size={20} />
          <input
            type="text"
            placeholder="Search by agent name..."
            className={styles.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className={styles.agentsList}>
          {loading ? (
            <div className={styles.stateText}>Searching agents...</div>
          ) : agents.length === 0 ? (
            <div className={styles.stateText}>No agents found.</div>
          ) : (
            agents.map((agent) => {
              const isSelected = localSelected.some((a) => a.id === agent.id);
              return (
                <div
                  key={agent.id}
                  className={`${styles.agentListItem} ${isSelected ? styles.selectedItem : ""}`}
                  onClick={() => toggleAgent(agent)}
                >
                  <div className={styles.agentInfoRow}>
                    <div className={styles.agentAvatarSmall}>
                      {agent.photo_url ? (
                        <img src={agent.photo_url} alt="" />
                      ) : (
                        <span>{agent.name.charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                    <span className={styles.agentListName}>{agent.name}</span>
                  </div>
                  <div className={styles.checkbox}>
                    {isSelected && <MdCheckCircle size={20} color="#1a1c1e" />}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className={styles.dialogFooter}>
          <span className={styles.countText}>
            {localSelected.length} / 15 selected
          </span>
          <button
            type="button"
            className={styles.saveBtn}
            onClick={() => {
              onSave(localSelected);
              onClose();
            }}
          >
            Confirm Selection
          </button>
        </div>
      </div>
    </div>
  );
}
